import { connect, ResolveThunks } from "react-redux";
import { useEffect, useState } from "react";
import { RootState } from "state/rootReducer";
import { actions as userActions } from "state/slices/userSlice";
import SongList from "components/SongList/SongList";
import { LoadingSpinner } from "components/LoadingSpinner/LoadingSpinner";
import { useOpenSong } from "shared/navigation";
import ImportPlaylistModal from "pages/ManageLikesPage/components/ImportPlaylistModal";
import FeedbackSelect from "pages/ManageLikesPage/components/FeedbackSelect";
import TagSelect from "pages/ManageLikesPage/components/TagSelect";
import { ICON_STROKE, ImportIcon } from "shared/icons";

interface StateProps {
  songFeedback: SongFeedback[] | null;
  possibleTags: Tag[];
}

interface DispatchProps {
  onFetchSongFeedback: typeof userActions.fetchSongFeedback;
  onAddFeedback: typeof userActions.addFeedback;
  onRemoveFeedback: typeof userActions.removeFeedback;
  onAddTag: typeof userActions.addTag;
  onRemoveTag: typeof userActions.removeTag;
  onFetchUserPlaylists: typeof userActions.fetchUserPlaylists;
  onImportPlaylist: typeof userActions.importUserPlaylist;
}

type ManageLikesPageProps = StateProps & ResolveThunks<DispatchProps>;

const REFRESH_INTERVAL = 5 * 60 * 1000;

function ManageLikesPage(props: ManageLikesPageProps): JSX.Element {
  const openSong = useOpenSong();

  const [shouldUpdate, setShouldUpdate] = useState(true);
  const [inImportModal, setInImportModal] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  function openImportModal() {
    props.onFetchUserPlaylists();

    setInImportModal(true);
  }

  useEffect(() => {
    if (props.songFeedback === null || shouldUpdate) {
      console.log("Fetching song feedback..");

      props.onFetchSongFeedback();

      setShouldUpdate(false);
    }
    console.log("Setting refresh timer for feedback");
    setTimeout(() => {
      console.log(`Setting shouldUpdate to true`);
      setShouldUpdate(true);
    }, REFRESH_INTERVAL);
  }, [shouldUpdate]);

  function selectSong(song: Song): void {
    openSong(song);
  }

  function render(): JSX.Element {
    return props.songFeedback === null ? (
      <LoadingSpinner border={false} />
    ) : (
      renderSongFeedback()
    );
  }

  function renderSongFeedback(): JSX.Element {
    return (
      <div className="p-2 sm:p-4 pb-0 w-full flex flex-col mt-4">
        {/* Flex centring: without it the svg sits inline on the text baseline,
            riding high and off-centre beside the label. */}
        <button
          type="button"
          className="button-generic flex items-center justify-center gap-2 mr-2 sm:mr-4 text-sm sm:text-lg mb-4"
          onClick={() => openImportModal()}
        >
          <ImportIcon size={17} strokeWidth={ICON_STROKE} aria-hidden="true" />
          Import from Spotify Playlist
        </button>
        <SongList
          songs={props.songFeedback!.map((sf) => sf.song)}
          selectSongHandler={(s) => selectSong(s)}
          deleteSongHandler={(s) => props.onRemoveFeedback(s.gid)}
          otherdata={props.songFeedback!.map((sf) => (
            <>
              <FeedbackSelect
                value={sf.feedback}
                songName={sf.song.name}
                onChange={(feedback) =>
                  props.onAddFeedback(sf.song.gid, feedback)
                }
              />
              <TagSelect
                tags={sf.tags ?? []}
                possibleTags={props.possibleTags}
                songName={sf.song.name}
                onAdd={(tag) => props.onAddTag(sf.song.gid, tag)}
                onRemove={(tag) => props.onRemoveTag(sf.song.gid, tag)}
              />
            </>
          ))}
          otherdataName="Feedback"
          otherdataVariant="controls"
          maxSongs={1000}
        />
        {inImportModal && (
          <ImportPlaylistModal
            isImporting={isImporting}
            onCancel={() => setInImportModal(false)}
            onImport={async (playlist) => {
              setIsImporting(true);
              await props.onImportPlaylist(playlist.id);
              setInImportModal(false);
              setIsImporting(false);
            }}
          />
        )}
      </div>
    );
  }

  return render();
}

function mapState(state: RootState): StateProps {
  return {
    songFeedback: state.user.songFeedbackByGid
      ? Object.entries(state.user.songFeedbackByGid).map(([_, sf]) => sf)
      : state.user.songFeedbackByGid,
    possibleTags: state.user.possibleTags || [],
  };
}

const mapDispatch: DispatchProps = {
  onFetchSongFeedback: userActions.fetchSongFeedback,
  onAddFeedback: userActions.addFeedback,
  onRemoveFeedback: userActions.removeFeedback,
  onAddTag: userActions.addTag,
  onRemoveTag: userActions.removeTag,
  onFetchUserPlaylists: userActions.fetchUserPlaylists,
  onImportPlaylist: userActions.importUserPlaylist,
};

export default connect(mapState, mapDispatch)(ManageLikesPage);
